import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, User, LogOut, ChevronDown, ShoppingCart, Phone, Car, Wrench, RectangleHorizontal, HandCoins } from 'lucide-react';
import { Button } from './ui/button';
import { RegionSelector } from './RegionSelector';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useCart } from '../contexts/CartContext';
import { HeaderSearch } from './search/HeaderSearch';
import { MobileSearchButton } from './search/SearchOverlay';

// Logos for different themes
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/gl2mwiwe_CLOUDGM%20MARKET%20WHITE.png"; // White text for dark mode
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/vu2e3mcb_CLOUDGM%20MARKET%20BLACK.png"; // Black text for light mode

export const Navbar = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const [mobileMarketOpen, setMobileMarketOpen] = useState(false);
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const isAr = language === 'ar';
  const cartCount = getCartCount();

  // Site name changes based on dark mode
  const siteName = isDarkMode ? 'كلاود جي ام' : 'CloudGM';

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  // Marketplace categories exposed in the "السوق" dropdown
  const marketItems = [
    { to: '/marketplace/numbers', label: isAr ? 'أرقام مميزة' : 'VIP Numbers', icon: Phone, testId: 'nav-market-numbers' },
    { to: '/marketplace/plates',  label: isAr ? 'لوحات سيارات' : 'Car Plates', icon: RectangleHorizontal, testId: 'nav-market-plates' },
    { to: '/marketplace/cars',    label: isAr ? 'السيارات' : 'Cars', icon: Car, testId: 'nav-market-cars' },
    { to: '/spare-parts',         label: isAr ? 'قطع غيار' : 'Spare Parts', icon: Wrench, testId: 'nav-market-parts' },
    { to: '/buy-requests',        label: isAr ? 'طلبات الشراء' : 'Buy Requests', icon: HandCoins, testId: 'nav-market-buy-requests' },
  ];

  const isMarketActive = location.pathname.startsWith('/marketplace') || location.pathname.startsWith('/spare-parts') || location.pathname.startsWith('/buy-requests');

  const navLinks = [
    { to: '/services', label: isAr ? 'الخدمات' : 'Services', testId: 'nav-services' },
    { to: '/products', label: isAr ? 'المتجر' : 'Store', testId: 'nav-store' },
    { to: '/properties', label: isAr ? 'العقارات' : 'Real Estate', testId: 'nav-real-estate' },
  ];

  return (
    <nav className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-all duration-300 ${isDarkMode ? 'bg-[#0a0a0a]/80 border-white/5' : 'bg-white/90 border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Header */}
        <div className="hidden md:flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3 group transition-all duration-300 hover:opacity-80" data-testid="logo-link">
            <img 
              src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} 
              alt="CloudGM Logo" 
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          <div className="flex items-center gap-1">
            {/* Services first (keep order) */}
            {navLinks.filter(l => l.to === '/services').map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:scale-105 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                data-testid={link.testId}
              >
                {link.label}
              </Link>
            ))}

            {/* Marketplace dropdown (replaces the standalone /marketplace page) */}
            <div
              className="relative"
              onMouseEnter={() => setMarketOpen(true)}
              onMouseLeave={() => setMarketOpen(false)}
            >
              <button
                type="button"
                data-testid="nav-marketplace"
                aria-haspopup="true"
                aria-expanded={marketOpen}
                onClick={() => setMarketOpen((v) => !v)}
                className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:scale-105 ${
                  isMarketActive
                    ? (isDarkMode ? 'text-white bg-white/5' : 'text-[#0f172a] bg-gray-100')
                    : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100')
                }`}
              >
                {isAr ? 'السوق' : 'Marketplace'}
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${marketOpen ? 'rotate-180' : ''}`} />
              </button>
              {marketOpen && (
                <div
                  className={`absolute top-full ${isAr ? 'right-0' : 'left-0'} mt-1 w-60 rounded-xl border shadow-2xl backdrop-blur-xl overflow-hidden animate-slideDown z-50 ${
                    isDarkMode ? 'bg-[#0a0a0a]/95 border-white/10' : 'bg-white/95 border-gray-200'
                  }`}
                  data-testid="nav-marketplace-menu"
                >
                  {marketItems.map((it) => {
                    const Icon = it.icon;
                    const active = location.pathname === it.to;
                    return (
                      <Link
                        key={it.to}
                        to={it.to}
                        onClick={() => setMarketOpen(false)}
                        data-testid={it.testId}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                          active
                            ? 'bg-[#1E9BD9]/10 text-[#1E9BD9]'
                            : (isDarkMode ? 'text-gray-300 hover:bg-white/5 hover:text-white' : 'text-gray-700 hover:bg-gray-100 hover:text-[#0f172a]')
                        }`}
                      >
                        <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <Icon className="h-4 w-4 text-[#1E9BD9]" />
                        </span>
                        <span className="font-medium">{it.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Remaining links (Store, Real Estate) */}
            {navLinks.filter(l => l.to !== '/services').map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:scale-105 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                data-testid={link.testId}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <HeaderSearch />
            <RegionSelector />

            {/* Cart Icon */}
            <Link to="/cart" className="relative" data-testid="nav-cart">
              <Button 
                variant="ghost" 
                size="icon"
                className={`relative ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -end-1 h-5 w-5 flex items-center justify-center rounded-full bg-[#1E9BD9] text-white text-xs font-bold">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Button>
            </Link>

            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/dashboard">
                  <Button 
                    variant="ghost" 
                    data-testid="nav-dashboard"
                    className={`${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                  >
                    <User className="h-4 w-4 me-2" />
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  data-testid="logout-btn"
                  className={`${isDarkMode ? 'border-white/10 text-gray-400 hover:text-white hover:bg-white/5 hover:border-white/20' : 'border-gray-200 text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    data-testid="nav-login"
                    className={`${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                  >
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white border-0 shadow-lg shadow-[#1E9BD9]/20 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#1E9BD9]/30" 
                    data-testid="nav-register"
                  >
                    {t('nav.register')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Header */}
        <div className="md:hidden flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="mobile-logo-link">
            <img 
              src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} 
              alt="CloudGM Logo" 
              className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          <div className="flex items-center gap-2">
            <MobileSearchButton className={`p-2 rounded-lg transition-all ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`} />
            {/* Mobile Cart Icon */}
            <Link to="/cart" className="relative" data-testid="mobile-nav-cart">
              <Button 
                variant="ghost" 
                size="icon"
                className={`relative ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -end-1 h-4 w-4 flex items-center justify-center rounded-full bg-[#1E9BD9] text-white text-[10px] font-bold">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Button>
            </Link>
            <RegionSelector compact={true} />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-lg transition-all ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className={`md:hidden absolute top-16 left-0 right-0 backdrop-blur-xl border-b shadow-2xl animate-slideDown ${isDarkMode ? 'bg-[#0a0a0a]/95 border-white/5' : 'bg-white/95 border-gray-200'}`}>
            <div className="px-4 py-6 space-y-2">
              {/* Services */}
              {navLinks.filter(l => l.to === '/services').map((link, index) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 animate-slideUp hover:translate-x-2 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`mobile-${link.testId}`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Marketplace mobile accordion */}
              <div className="animate-slideUp" style={{ animationDelay: '50ms' }}>
                <button
                  type="button"
                  data-testid="mobile-nav-marketplace"
                  onClick={() => setMobileMarketOpen(v => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all duration-300 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                >
                  <span>{isAr ? 'السوق' : 'Marketplace'}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileMarketOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileMarketOpen && (
                  <div className={`mt-1 ms-3 ps-3 border-s space-y-1 ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    {marketItems.map((it) => {
                      const Icon = it.icon;
                      return (
                        <Link
                          key={it.to}
                          to={it.to}
                          onClick={() => { setIsOpen(false); setMobileMarketOpen(false); }}
                          data-testid={`mobile-${it.testId}`}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium transition-all ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                        >
                          <Icon className="h-4 w-4 text-[#1E9BD9]" />
                          <span>{it.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Store + Real Estate */}
              {navLinks.filter(l => l.to !== '/services').map((link, index) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 animate-slideUp hover:translate-x-2 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                  style={{ animationDelay: `${(index + 2) * 50}ms` }}
                  data-testid={`mobile-${link.testId}`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className={`border-t pt-4 mt-4 ${isDarkMode ? 'border-white/5' : 'border-gray-200'}`}>
                {user ? (
                  <div className="space-y-2">
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center px-4 py-3 rounded-lg font-medium transition-all ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                      data-testid="mobile-nav-dashboard"
                    >
                      <User className="h-5 w-5 me-3" />
                      {t('nav.dashboard')}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg font-medium transition-all"
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="h-5 w-5 me-3" />
                      {t('nav.logout')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className={`block px-4 py-3 rounded-lg font-medium text-center transition-all ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}
                      data-testid="mobile-nav-login"
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-3 bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white rounded-lg font-medium text-center"
                      data-testid="mobile-nav-register"
                    >
                      {t('nav.register')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
